import { Anchor, Box, Heading, Paragraph, Tag } from '@zillow/constellation-10';
import { PostReadTime } from '@zillow/zrs-cms';
import { uniqBy } from 'lodash';
import React from 'react';

import { BaseTerm, Post } from '../../../types/shared';
import { NON_GRAY_TONES, TagTone } from '../../../util/tagTones';
import { wrap } from '../../../util/wrap';

interface PostHeaderProps {
    post: Post;
}

const PostHeader: React.FC<PostHeaderProps> = ({ post }) => {
    const excerpt = post.excerpt?.raw?.replace(/(<([^>]+)>)/gi, '') || '';
    const categories = uniqBy(
        [post.primaryCategory, ...post.categories],
        'id'
    ).filter((category): category is BaseTerm => Boolean(category));

    return (
        <Box
            css={{
                maxWidth: 'breakpoint-lg',
                width: '100%',
                xl: {
                    paddingRight: 'layout.vast.default',
                },
            }}
        >
            <Box
                css={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'tighter',
                    justifyContent: 'flex-start',
                    marginBottom: 'default',
                    lg: {
                        gap: 'default',
                    },
                    xxl: {
                        marginBottom: 'looser',
                        textAlign: 'left',
                    },
                }}
            >
                {categories?.map((category, index) => (
                    <Anchor
                        key={category.id}
                        href={category.link}
                        css={{ textDecoration: 'none' }}
                    >
                        <Tag
                            css={{ textTransform: 'none' }}
                            tone={
                                NON_GRAY_TONES[
                                    wrap(0, NON_GRAY_TONES.length, index)
                                ] as TagTone
                            }
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: category.name,
                                }}
                            />
                        </Tag>
                    </Anchor>
                ))}
                <PostReadTime readTime={post.readTime} />
            </Box>
            <Box
                css={{
                    paddingRight: 'loose',
                    xxl: {
                        textAlign: 'left',
                    },
                }}
            >
                {/* @ts-expect-error - Heading requires children but we're using dangerouslySetInnerHTML */}
                <Heading
                    level={1}
                    css={{
                        color: 'inherit',
                        textStyle: {
                            base: 'heading-sm',
                            lg: 'heading-md',
                        },
                        marginBottom: {
                            base: 'default',
                            xxl: 'looser',
                        },
                    }}
                    data-epi-type="title"
                    dangerouslySetInnerHTML={{ __html: post.title?.raw }}
                />
                {!!excerpt && (
                    // @ts-expect-error - Paragraph requires children but we're using dangerouslySetInnerHTML
                    <Paragraph
                        css={{
                            color: 'text.subtle',
                            textStyle: {
                                base: 'body',
                                lg: 'body-lg',
                            },
                            marginBottom: {
                                base: 'loose',
                                lg: 'looser',
                            },
                        }}
                        dangerouslySetInnerHTML={{ __html: excerpt }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default PostHeader;
